import { GlobeAltIcon, TagIcon } from "@heroicons/react/outline";
import { PlusIcon } from "@heroicons/react/solid";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import VariableList from "@/components/InputVariableConfigurator/ConfiguratorContent/VariableList";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { validUrl } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useAutoScroll from "@/hooks/useAutoScroll";
import { EndpointConfiguration } from "@/types/evaluate";

const validationSchema = createBlockSchema<EndpointConfiguration>()
  .requireName()
  .addRule(validUrl("configuration.url", "Must be a valid URL"))
  .build();

const EndpointBlockCore = observer(
  (_props: Omit<BlockProps<EndpointConfiguration>, "DefaultFields">) => {
    const blockStore = useBlock<EndpointConfiguration>();
    const isReadonly = useIsReadonly();
    const ref = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<number>(0);

    // Keep headers in local state to prevent re-mounting
    const [localHeaders, setLocalHeaders] = useState<[string, string][]>([]);

    useEffect(() => {
      if (!blockStore.configuration) {
        blockStore.setConfiguration({
          url: "",
          headers: {},
        });
      }
    }, [blockStore]);

    // Sync local headers with store on initialization

    useEffect(() => {
      if (blockStore.configuration?.headers) {
        const headersArray = Object.entries(blockStore.configuration.headers);
        setLocalHeaders(headersArray);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Create stable variables array with consistent IDs
    const variables = useMemo(
      () =>
        localHeaders.map((header, index) => ({
          key: header[0],
          value: header[1],
          id: `header-${index}`, // Use index for stable IDs
        })),
      [localHeaders],
    );

    const { lastItemRef } = useAutoScroll(variables, ref);

    useLayoutEffect(() => {
      const scrollContainer = ref.current;
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollPositionRef.current;
      }
    });

    const handleScroll = useCallback(() => {
      if (ref.current) {
        scrollPositionRef.current = ref.current.scrollTop;
      }
    }, []);

    const updateStore = useCallback(() => {
      if (
        blockStore.configuration &&
        (localHeaders.length > 0 || Object.keys(blockStore.configuration.headers || {}).length > 0)
      ) {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          url: blockStore.configuration?.url || "",
          headers: Object.fromEntries(localHeaders),
        });
      }
    }, [localHeaders, blockStore]);

    useEffect(() => {
      updateStore();
    }, [updateStore]);

    const handleUrlChange = useCallback(
      (url: string) => {
        const trimmedUrl = url.trim();
        blockStore.setConfiguration({
          ...blockStore.configuration,
          url: trimmedUrl,
          headers: blockStore.configuration?.headers || {},
        });
      },
      [blockStore],
    );

    const handleAddHeader = useCallback(() => {
      setLocalHeaders(prev => [...prev, ["", ""]]);
    }, []);

    const handleUpdateVariable = useCallback((variable: Variable, index: number) => {
      const keyValue = String(variable.key || "");
      const stringValue =
        typeof variable.value === "object"
          ? JSON.stringify(variable.value)
          : String(variable.value || "");

      setLocalHeaders(prev => {
        const newHeaders = [...prev];
        if (index < newHeaders.length) {
          newHeaders[index] = [keyValue, stringValue];
        }
        return newHeaders;
      });
    }, []);

    const handleRemoveVariable = useCallback((index: number) => {
      setLocalHeaders(prev => prev.filter((_, i) => i !== index));
    }, []);

    return (
      <>
        <SectionGroup
          title="Endpoint Configuration"
          description="Configure the HTTP endpoint and request headers"
          icon={<GlobeAltIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="Endpoint URL" path="configuration.url">
              <Input
                className="w-full"
                value={blockStore.configuration?.url || ""}
                placeholder="https://api.example.com/promptlayer"
                onChange={e => handleUrlChange(e.target.value)}
                disabled={isReadonly}
              />
            </ValidatedField>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Request Headers"
          description="Add custom headers for authentication and request configuration"
          icon={<TagIcon className="h-4 w-4" />}
        >
          <ValidatedField path="" variant="sources">
            <div className="flex flex-col gap-y-2">
              <div ref={ref} className="max-h-[170px] overflow-y-auto" onScroll={handleScroll}>
                <VariableList
                  showAddFileButton={false}
                  variables={variables}
                  placeholder="<API_KEY>"
                  keyPlaceholder="X-API-Key"
                  tooltip="Header value"
                  keyTooltip="Header name"
                  removeTooltip="Remove header"
                  readOnly={isReadonly}
                  updateInputVariable={handleUpdateVariable}
                  removeInputVariable={handleRemoveVariable}
                  lastItemRef={lastItemRef}
                  scrollContainerRef={ref}
                />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleAddHeader}
                disabled={isReadonly}
              >
                <PlusIcon className="mr-2 h-5 w-5 text-gray-600" />
                Add Header
              </Button>
            </div>
          </ValidatedField>
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<EndpointConfiguration>(EndpointBlockCore, {
  validationSchema,
});
